<template>
  <TooltipStandardV2
    :disabled-tooltip="!(overflowing && props.overflowTooltip)"
    :text="text"
    arrow-position="top-center"
    position-absolute
  >
    <span
      ref="textRef"
      :lang="locale"
      :class="[
        'text',
        {
          text__display: type === 'display',
          text__h1: type === 'h1',
          text__h2: type === 'h2',
          text__h3: type === 'h3',
          text__h4: type === 'h4',
          text__h5: type === 'h5',
          text__paragraph: type === 'paragraph',
          text__label: type === 'label',
          text__tiny: type === 'tiny',
          text__bold: weight === 'bold',
          text__color_secondary: color === 'secondary',
          text__underline: underline,
          text__disabled: disabled
        }
      ]"
      :style="{ ...(color !== 'primary' && color !== 'secondary' ? { color } : {}), ...styles }"
    >
      {{ text }}
    </span>
  </TooltipStandardV2>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, onUnmounted, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import TooltipStandardV2 from './TooltipStandardV2.vue'

export type TextType = 'display' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'paragraph' | 'label' | 'tiny'

export type TextWeight = 'regular' | 'bold'

export type TextColor = 'primary' | 'secondary' | string

export type TextOverflow = 'ellipsis' | 'break-word'

export type TextWrap = 'nowrap' | 'wrap'

export type Props = {
  type?: TextType
  overflow?: TextOverflow
  weight?: TextWeight
  underline?: boolean
  color?: TextColor
  disabled?: boolean
  text?: string
  textWrap?: TextWrap
  overflowTooltip?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'paragraph',
  weight: 'regular',
  color: 'primary',
  overflow: 'break-word',
  textWrap: 'wrap',
  text: '-',
  underline: false,
  disabled: false,
  overflowTooltip: false
})

const { locale } = useI18n()

const textRef = ref<HTMLElement>()

const overflowing = ref<boolean>(false)

const styles = computed<object>((): object => ({
  ...(props.overflow === 'ellipsis'
    ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textWrap: 'nowrap',
        whiteSpace: 'nowrap',
        width: '100%'
      }
    : {}),
  textWrap: props.textWrap
}))

const update = () => {
  if (textRef.value) {
    const textElement = textRef.value
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _ = textElement.offsetWidth // reading offsetWidth can help flush style changes
    overflowing.value = textElement.scrollWidth > textElement.clientWidth
  }
}

let ro: ResizeObserver | null = null
onMounted((): void => {
  if (props.overflow === 'ellipsis' && textRef.value) {
    ro = new ResizeObserver((): void => {
      update()
    })
    ro.observe(textRef.value)
  }
})

onUnmounted((): void => {
  if (ro && textRef.value) {
    ro.unobserve(textRef.value)
    ro.disconnect()
    ro = null
  }
})

watchEffect(() => update())
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/variables.scss';

.text {
  display: inline-block;
  color: variables.$color-text-primary;
  -webkit-hyphens: auto;
  -moz-hyphens: auto;
  -ms-hyphens: auto;
  hyphens: auto;
  word-break: break-word;

  &__color_secondary {
    color: variables.$color-text-secondary;
  }

  &__display {
    @include font-mixins.desktop-regular-display;

    &.text__bold {
      @include font-mixins.desktop-bold-display;
    }
  }

  &__h1 {
    @include font-mixins.desktop-regular-h1;

    &.text__bold {
      @include font-mixins.desktop-bold-h1;
    }
  }

  &__h2 {
    @include font-mixins.desktop-regular-h2;

    &.text__bold {
      @include font-mixins.desktop-bold-h2;
    }
  }

  &__h3 {
    @include font-mixins.desktop-regular-h3;

    &.text__bold {
      @include font-mixins.desktop-bold-h3;
    }
  }

  &__h4 {
    @include font-mixins.desktop-regular-h4;

    &.text__bold {
      @include font-mixins.desktop-bold-h4;
    }
  }

  &__h5 {
    @include font-mixins.desktop-regular-h5;

    &.text__bold {
      @include font-mixins.desktop-bold-h5;
    }
  }

  &__paragraph {
    @include font-mixins.desktop-regular-paragraph;

    &.text__bold {
      @include font-mixins.desktop-bold-paragraph;
    }
  }

  &__label {
    @include font-mixins.desktop-regular-label;

    &.text__bold {
      @include font-mixins.desktop-bold-label;
    }
  }

  &__tiny {
    @include font-mixins.desktop-regular-tiny;

    &.text__bold {
      @include font-mixins.desktop-bold-tiny;
    }
  }

  &__underline {
    text-decoration: underline;
  }

  &__disabled {
    color: variables.$color-text-secondary;
  }
}
</style>
